import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import hoteltemplateService from '../services/hoteltemplateService';
import vaultsourcegroupService from '../services/vaultsourcegroupService';
import vaultsourceService from '../services/vaultsourceService';
import vaultsqlService from '../services/vaultsqlService';
import canonicalgroupService from '../services/canonicalgroupService';
import hotelService from '../services/hotelService';
import accountService from '../services/accountService';
import TemplateAssistantChat from './TemplateAssistantChat';
import { renderTemplate, openPrintWindow, flattenObject, amountFormatOf, formatAmount } from '../utils/templateRender';
import ConfirmModal from './shared/ConfirmModal';
import './AccountForm.css';

// A FULL DOCUMENT TEMPLATE GETS ITS OWN SCREEN, not a panel under a table.
//
// A folio template is a page of HTML built against a nested canonical record, usually by copying a PDF
// the hotel already issues. That work needs the editor, a live preview and the assistant conversation
// visible at once — three things that do not fit beside a list, and none of which can be abandoned
// halfway by clicking a row underneath them.

const HOTEL_FIELDS = [
  'hotel.logo', 'hotel.hotelname', 'hotel.hotelcode',
  'hotel.ADDRESS1', 'hotel.ADDRESS2', 'hotel.CITY', 'hotel.STATE', 'hotel.ZIPCODE',
  'hotel.PHONE', 'hotel.FAX', 'hotel.WEBSITE',
  'hotel.COUNTRY', 'hotel.CURRENCY',
];

// Column names out of a group's saved SQL — the flat fields a search result row carries, which is half
// of what a template can reference (the other half is the canonical payload's nested paths).
function extractSqlColumns(sql) {
  if (!sql) return [];
  const match = sql.match(/SELECT\s+([\s\S]+?)\s+FROM\b/i);
  if (!match) return [];
  const cols = [];
  let depth = 0, current = '';
  for (const ch of match[1]) {
    if (ch === '(') { depth++; current += ch; }
    else if (ch === ')') { depth--; current += ch; }
    else if (ch === ',' && depth === 0) { cols.push(current.trim()); current = ''; }
    else { current += ch; }
  }
  if (current.trim()) cols.push(current.trim());
  return cols.map(col => {
    const asMatch = col.match(/\bAS\s+`?(\w+)`?\s*$/i);
    if (asMatch) return asMatch[1];
    const parts = col.trim().split(/[\s.]+/);
    return parts[parts.length - 1].replace(/[`'"]/g, '') || null;
  }).filter(c => c && c !== '*');
}

// Every dot path in a sample payload, so the assistant is told what it may reference rather than left to
// infer it. Arrays contribute one representative path (index 0) plus the `#each` path itself.
function payloadPaths(value, prefix = '', out = [], depth = 0) {
  if (depth > 6 || value === null || value === undefined) return out;
  if (Array.isArray(value)) {
    if (prefix) out.push(prefix);
    if (value.length) payloadPaths(value[0], `${prefix}.0`, out, depth + 1);
    return out;
  }
  if (typeof value === 'object') {
    for (const [k, v] of Object.entries(value)) {
      const path = prefix ? `${prefix}.${k}` : k;
      if (v !== null && typeof v === 'object') payloadPaths(v, path, out, depth + 1);
      else out.push(path);
    }
    return out;
  }
  if (prefix) out.push(prefix);
  return out;
}

// HOW AMOUNTS PRINT (V619) — the choices the lambda accepts. '' = as stored / no separator.
const DECIMAL_OPTIONS = [['', 'As stored (285, 237.5)'], ['0', '0 (285)'], ['1', '1 (285.0)'], ['2', '2 (285.00)'], ['3', '3'], ['4', '4']];
const DECIMAL_SEPARATOR_OPTIONS = [['.', 'Point (285.00)'], [',', 'Comma (285,00)']];
const THOUSANDS_OPTIONS = [['', 'None (1234)'], [',', 'Comma (1,234)'], ['.', 'Point (1.234)'], [' ', 'Space (1 234)'], ["'", "Apostrophe (1'234)"]];
const FORMAT_FIELDS = ['amount_decimals', 'decimal_separator', 'thousands_separator', 'show_zero_lines'];
const formatFieldsOf = (t) => ({
  amount_decimals: t?.amount_decimals === null || t?.amount_decimals === undefined ? '' : String(t.amount_decimals),
  decimal_separator: t?.decimal_separator || '.',
  thousands_separator: t?.thousands_separator || '',
  show_zero_lines: t?.show_zero_lines === 'N' ? 'N' : 'Y',
});

const HotelFullTemplateForm = () => {
  const { hotelId, id } = useParams();
  const navigate = useNavigate();
  const isCreate = !id || id === 'new';

  const [form, setForm] = useState({ vault_source_group_id: '', name: '', content: '', show_line_vat: 'N', show_line_remarks: 'N', ...formatFieldsOf(null) });
  const [groupLabelText, setGroupLabelText] = useState('');
  const [vaultSourceGroups, setVaultSourceGroups] = useState([]);
  const [vaultSources, setVaultSources] = useState([]);
  const [hotelRecord, setHotelRecord] = useState(null);
  const [accountLogo, setAccountLogo] = useState(null);
  const [sqlFields, setSqlFields] = useState([]);
  const [canonicalPayload, setCanonicalPayload] = useState(null);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(!isCreate);
  const [confirmModal, setConfirmModal] = useState(null);

  useEffect(() => {
    vaultsourcegroupService.getAllVaultSourceGroups()
      .then(r => setVaultSourceGroups(r.vaultsourcegroups || []))
      .catch(() => {});
    vaultsourceService.getAllVaultSources()
      .then(r => setVaultSources(r.vaultsources || []))
      .catch(() => {});
    hotelService.getHotelById(hotelId)
      .then(r => {
        const hotel = r.hotel || r;
        setHotelRecord(hotel || null);
        if (hotel?.accountid) {
          accountService.getAccountById(hotel.accountid)
            .then(acc => setAccountLogo(acc?.account?.logo ?? acc?.logo ?? null))
            .catch(() => {});
        }
      })
      .catch(() => {});
  }, [hotelId]);

  useEffect(() => {
    if (isCreate) return;
    hoteltemplateService.getFullById(id)
      .then(r => {
        const t = r.data?.hotelfulltemplate;
        if (t) {
          setForm({
            vault_source_group_id: t.vault_source_group_id,
            name: t.name || '',
            content: t.content || '',
            show_line_vat: t.show_line_vat === 'Y' ? 'Y' : 'N',
            show_line_remarks: t.show_line_remarks === 'Y' ? 'Y' : 'N',
            ...formatFieldsOf(t),
          });
          setGroupLabelText(t.vault_source_name ? `${t.vault_source_name} — ${t.vault_source_group_name}` : t.vault_source_group_name);
        }
      })
      .catch(err => setError(err.response?.data?.error?.message || 'Could not load this template'))
      .finally(() => setLoading(false));
  }, [id, isCreate]);

  // WHAT THE TEMPLATE MAY REFERENCE, for this system group: the flat columns its saved queries return,
  // and the canonical record the folio builder produces. Both are handed to the assistant — a variable it
  // invents renders as literal braces on a guest's invoice, so it is given the real list instead.
  useEffect(() => {
    const groupId = form.vault_source_group_id;
    if (!groupId) { setSqlFields([]); setCanonicalPayload(null); return; }
    (async () => {
      try {
        const sqlRes = await vaultsqlService.getAllVaultSqls({ vaultsourcegroupid: groupId });
        const cols = (sqlRes.vaultsqls || []).flatMap(s => extractSqlColumns(s.sql_source));
        setSqlFields([...new Set(cols)]);
      } catch (e) { setSqlFields([]); }

      try {
        // The canonical group carries the sample payload. It is matched to the system group BY NAME,
        // which is how the rest of the portal pairs the two (see HotelTemplates).
        const group = vaultSourceGroups.find(g => String(g.id) === String(groupId));
        if (!group?.name) return;
        const cgRes = await canonicalgroupService.getAllCanonicalGroups();
        const cg = (cgRes.data?.canonicalgroups || []).find(c => (c.name || '').toLowerCase() === group.name.toLowerCase());
        if (!cg) return;
        const full = await canonicalgroupService.getCanonicalGroupById(cg.id);
        const payload = full.data?.payload ?? full.data?.sample_payload ?? null;
        setCanonicalPayload(typeof payload === 'string' ? tryParse(payload) : payload);
      } catch (e) { setCanonicalPayload(null); }
    })();
  }, [form.vault_source_group_id, vaultSourceGroups]);

  const tryParse = (s) => { try { return JSON.parse(s); } catch (e) { return s; } };

  const groupLabel = (group) => {
    const source = vaultSources.find(s => String(s.id) === String(group.vaultsourceid));
    return source ? `${source.name} — ${group.name}` : group.name;
  };

  const getCountryName = (code) => {
    if (!code) return '';
    try {
      return new Intl.DisplayNames(['en'], { type: 'region' }).of(code) || code;
    } catch {
      return code;
    }
  };

  // The preview substitutes what this screen actually knows — the hotel's own details — and leaves every
  // record-level token visible as {{...}} so it is obvious which parts come from the folio being printed.
  const substitutePreview = (html) => {
    if (!html) return '';
    const h = hotelRecord || {};
    const replacements = {
      'account.logo': accountLogo || '',
      'hotel.logo': h.logo || '',
      'hotel.hotelname': h.hotelname || h.HOTELNAME || 'Example Hotel Name',
      'hotel.hotelcode': h.hotelcode || h.HOTELCODE || '',
      'hotel.ADDRESS1': h.ADDRESS1 || 'Example Address',
      'hotel.ADDRESS2': h.ADDRESS2 || '',
      'hotel.CITY': h.CITY || 'Example City',
      'hotel.STATE': h.STATE || '',
      'hotel.ZIPCODE': h.ZIPCODE || 'Example Zip',
      'hotel.PHONE': h.PHONE || '',
      'hotel.FAX': h.FAX || '',
      'hotel.WEBSITE': h.WEBSITE || '',
      'hotel.COUNTRY': getCountryName(h.COUNTRY),
      'hotel.CURRENCY': h.CURRENCY || '',
    };
    return html.replace(/\{\{([\w.]+)\}\}/g, (_, key) =>
      key in replacements ? replacements[key] : `{{${key}}}`
    );
  };

  const availableFields = [
    'account.logo',
    ...HOTEL_FIELDS,
    ...sqlFields,
    ...(canonicalPayload && typeof canonicalPayload === 'object' ? payloadPaths(canonicalPayload) : []),
  ];

  // GENERATE — render this template against the canonical sample and open the print dialog.
  //
  // The preview above substitutes only the hotel's own details and leaves every record token visible, so
  // the author can see which parts come from the folio. This answers the other question: what does the
  // finished document look like for a real record. It uses the SAME renderer the vault screens print with
  // (utils/templateRender), against the canonical group's sample payload — the one on
  // /admin/canonical/<id>/canonicalgroup/<id> — so what appears here is what a guest would receive,
  // give or take that record's own values.
  //
  // The browser's print dialog is where the PDF comes from; a page cannot write one directly.
  const handleGenerate = () => {
    if (!form.content) {
      setError('There is no template to generate from yet.');
      return;
    }
    if (!canonicalPayload || typeof canonicalPayload !== 'object') {
      setError('This system group has no canonical sample payload, so there is nothing to render against. Add one on the canonical group.');
      return;
    }
    setError(null);
    // The flat top-level values double as the "row" a vault search would supply, so templates written
    // against either shape render here.
    const row = flattenObject(canonicalPayload);
    const html = renderTemplate(form.content, {
      // showLineVat travels on the row, which is where the renderer looks for document-level flags — the
      // same way the vault screen passes it, so Generate previews what a real folio will print.
      row: { ...row, showLineVat: form.show_line_vat, showLineRemarks: form.show_line_remarks },
      payload: canonicalPayload,
      scopes: { hotel: hotelRecord, account: { logo: accountLogo } },
      amountFormat: amountFormatOf(form),
    });
    openPrintWindow({ name: form.name || 'Example document', html });
  };

  const handleSave = async () => {
    if (!form.vault_source_group_id) {
      setError('System group is required');
      return;
    }
    // A hotel ends up with several of these and the list is read by name; an unnamed row is a document
    // nobody can identify without opening it.
    if (!form.name.trim()) {
      setError('Name is required');
      return;
    }
    setSaving(true);
    setError(null);
    try {
      if (isCreate) {
        await hoteltemplateService.createFull({
          hotel_id: parseInt(hotelId),
          vault_source_group_id: parseInt(form.vault_source_group_id),
          name: form.name.trim(),
          content: form.content || null,
          show_line_vat: form.show_line_vat,
          show_line_remarks: form.show_line_remarks,
          ...Object.fromEntries(FORMAT_FIELDS.map(k => [k, form[k]])),
        });
      } else {
        await hoteltemplateService.updateFull(id, {
          name: form.name.trim(),
          content: form.content || null,
          show_line_vat: form.show_line_vat,
          show_line_remarks: form.show_line_remarks,
          ...Object.fromEntries(FORMAT_FIELDS.map(k => [k, form[k]])),
        });
      }
      // Back to the TEMPLATES TAB, not just to the hotel: /hotel/:id opens on Vaults, so returning there
      // dropped the user somewhere they had not been.
      navigate(`/hotel/${hotelId}?tab=templates`);
    } catch (err) {
      setError(err.response?.data?.error?.message || err.message || 'Failed to save');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = () => {
    setConfirmModal({
      title: 'Delete Full Template',
      message: `Delete the ${groupLabelText || 'selected'} full template for this hotel? This cannot be undone.`,
      confirmLabel: 'Delete',
      danger: true,
      onConfirm: async () => {
        try {
          await hoteltemplateService.deleteFull(id);
          navigate(`/hotel/${hotelId}?tab=templates`);
        } catch (err) {
          setError(err.response?.data?.error?.message || 'Failed to delete');
        }
      },
    });
  };

  if (loading) return <div className="account-form-container"><p>Loading…</p></div>;

  return (
    <div className="account-form-container">
      {/* STICKY, on this form only. The actions live in the header, and the body below is a 22-row
          template editor, an insert panel and a rendered preview — so by the time you have made a change
          the Save button is several screens above you and the page reads as having none. Scoped with a
          modifier rather than changing .account-form-header, which two other screens share and neither
          of which is long enough to need it. */}
      <div className="account-form-header account-form-header-sticky">
        <h2>{isCreate ? 'Add Full Template' : 'Full Template'}</h2>
        <div className="form-actions">
          <button
            type="button"
            className="btn-secondary"
            onClick={handleGenerate}
            disabled={!form.content}
            title="Render this template against the canonical sample and open the print dialog"
          >
            Generate
          </button>
          <button type="button" className="btn-primary" onClick={handleSave} disabled={saving}>
            {saving ? 'Saving...' : isCreate ? 'Create Full Template' : 'Update Full Template'}
          </button>
          {!isCreate && (
            <button type="button" className="btn-danger" onClick={handleDelete}>Delete</button>
          )}
          <button type="button" className="btn-secondary" onClick={() => navigate(`/hotel/${hotelId}?tab=templates`)}>Cancel</button>
        </div>
      </div>

      {error && <div className="form-error">{error}</div>}

      <div className="form-row">
        <div className="form-group">
          <label>System Group <span className="required">*</span></label>
          {isCreate ? (
            <select
              value={form.vault_source_group_id}
              onChange={e => setForm(prev => ({ ...prev, vault_source_group_id: e.target.value }))}
            >
              <option value="">Select a system group...</option>
              {vaultSourceGroups.map(g => (
                <option key={g.id} value={g.id}>{groupLabel(g)}</option>
              ))}
            </select>
          ) : (
            // Fixed after creation: the group is what the template IS FOR, and changing it would point an
            // existing document at a different data set without changing a line of its HTML.
            <input type="text" value={groupLabelText} disabled />
          )}
        </div>
        <div className="form-group">
          {/* A per-template switch, because whether a folio itemises tax is a property of THAT document —
              the same hotel can have one layout that does and one that does not. The renderer reads it as
              {{#if showLineVat}} around the VAT column; when it is off the column is not rendered at all,
              rather than shown empty. The VAT total is in the totals block either way. */}
          <label>Show VAT/GST per line item</label>
          <div style={{ display: 'flex', alignItems: 'center', gap: '0.6rem' }}>
            <button
              type="button"
              className={form.show_line_vat === 'Y' ? 'btn-primary' : 'btn-secondary'}
              onClick={() => setForm(p => ({ ...p, show_line_vat: p.show_line_vat === 'Y' ? 'N' : 'Y' }))}
            >
              {form.show_line_vat === 'Y' ? 'Yes' : 'No'}
            </button>
            <span className="no-data-hint" style={{ margin: 0 }}>
              {form.show_line_vat === 'Y'
                ? 'Each charge shows its own VAT in a column beside the amount.'
                : 'Charges only — VAT appears in the totals block.'}
            </span>
          </div>
        </div>

        <div className="form-group">
          {/* HOW AMOUNTS PRINT — a setting of THIS document, applied by the shared renderer to every value
              whose field name ends in "amount". Blank decimals prints values exactly as stored. */}
          <label>Amount format</label>
          <div style={{ display: 'flex', gap: '0.6rem', flexWrap: 'wrap', alignItems: 'center' }}>
            {[['amount_decimals', 'Decimal places', DECIMAL_OPTIONS],
              ['decimal_separator', 'Decimal separator', DECIMAL_SEPARATOR_OPTIONS],
              ['thousands_separator', 'Thousands separator', THOUSANDS_OPTIONS]].map(([key, title, options]) => (
              <select key={key} title={title} value={form[key]}
                      onChange={e => setForm(p => ({ ...p, [key]: e.target.value }))}>
                {options.map(([v, l]) => <option key={v} value={v}>{title}: {l}</option>)}
              </select>
            ))}
            <span className="no-data-hint" style={{ margin: 0 }}>
              {form.thousands_separator && form.thousands_separator === form.decimal_separator
                ? 'The decimal and thousands separators must differ.'
                : `1234.5 prints as ${formatAmount('1234.5', 'amount', amountFormatOf(form)) ?? '1234.5'}`}
            </span>
          </div>
        </div>

        <div className="form-group">
          {/* Opera's own folio has this switch: whether a charge line prints the remark on its posting
              ("[NA Room]", "NA flags next day break", a POS check reference). The template reads it as
              {{#if showLineRemarks}} around {{reference}}. */}
          <label>Show line remarks</label>
          <div style={{ display: 'flex', alignItems: 'center', gap: '0.6rem' }}>
            <button
              type="button"
              className={form.show_line_remarks === 'Y' ? 'btn-primary' : 'btn-secondary'}
              onClick={() => setForm(p => ({ ...p, show_line_remarks: p.show_line_remarks === 'Y' ? 'N' : 'Y' }))}
            >
              {form.show_line_remarks === 'Y' ? 'Yes' : 'No'}
            </button>
            <span className="no-data-hint" style={{ margin: 0 }}>
              {form.show_line_remarks === 'Y'
                ? 'Each charge prints its posting remark under the description.'
                : 'Descriptions only.'}
              {' '}Wrap the remark in <code>{'{{#if showLineRemarks}}…{{/if}}'}</code>.
            </span>
          </div>
        </div>

        <div className="form-group">
          {/* Off: a line whose amounts are all zero (a 0.00 package component, a zero posting) is left out of
              every repeated list in the document. Applied by the shared renderer, like the amount format. */}
          <label>Show zero amounts</label>
          <div style={{ display: 'flex', alignItems: 'center', gap: '0.6rem' }}>
            <button
              type="button"
              className={form.show_zero_lines === 'Y' ? 'btn-primary' : 'btn-secondary'}
              onClick={() => setForm(p => ({ ...p, show_zero_lines: p.show_zero_lines === 'Y' ? 'N' : 'Y' }))}
            >
              {form.show_zero_lines === 'Y' ? 'Yes' : 'No'}
            </button>
            <span className="no-data-hint" style={{ margin: 0 }}>
              {form.show_zero_lines === 'Y'
                ? 'Lines with a zero amount are printed.'
                : 'Lines whose amounts are all zero are left out.'}
            </span>
          </div>
        </div>

        <div className="form-group">
          <label>Name <span className="required">*</span></label>
          <input
            type="text"
            value={form.name}
            onChange={e => setForm(prev => ({ ...prev, name: e.target.value }))}
            placeholder="e.g. Folio (guest copy)"
          />
        </div>
      </div>

      {/* Assistant on the left, template and preview on the right — the conversation stays visible while
          its output is being read, which is the whole reason this is a screen and not a modal. */}
      <div className="full-template-workspace">
        <div className="full-template-chat">
          <TemplateAssistantChat
            target="full"
            title="Template Assistant"
            allowAttachments
            height={520}
            availableFields={availableFields}
            context={{ canonicalPayload, currentContent: form.content || '' }}
            onInsert={html => setForm(prev => ({ ...prev, content: html }))}
          />
          <p className="no-data-hint" style={{ marginTop: '0.5rem' }}>
            Attach a PDF of an existing folio. The assistant reads it, rebuilds the layout as HTML and maps
            each value onto the canonical fields for this system group; whatever it produces lands in the
            editor beside it.
          </p>
        </div>

        <div className="full-template-editor">
          <div className="form-group">
            <label>Template HTML</label>
            <textarea
              className="account-template-html"
              value={form.content}
              onChange={e => setForm(prev => ({ ...prev, content: e.target.value }))}
              placeholder="The assistant writes here, or paste your own HTML. Values use {{token}} syntax, e.g. {{guestName}} or {{reservationFolioInformation.reservationInfo.roomStay.roomId}}."
              spellCheck={false}
              rows={22}
            />
          </div>
          {form.content && (
            <div className="form-group">
              <label>Preview</label>
              <iframe
                className="account-template-preview-iframe"
                srcDoc={`<!DOCTYPE html><html><head><meta charset="utf-8"><meta name="referrer" content="no-referrer"></head><body style="margin:0;padding:8px;">${substitutePreview(form.content)}</body></html>`}
                title="Full Template Preview"
                onLoad={e => { const f = e.target; if (f.contentDocument && f.contentDocument.body) f.style.height = f.contentDocument.body.scrollHeight + 'px'; }}
              />
            </div>
          )}
        </div>
      </div>

      {confirmModal && (
        <ConfirmModal
          title={confirmModal.title}
          message={confirmModal.message}
          confirmLabel={confirmModal.confirmLabel}
          danger={confirmModal.danger}
          onConfirm={() => { confirmModal.onConfirm(); setConfirmModal(null); }}
          onCancel={() => setConfirmModal(null)}
        />
      )}
    </div>
  );
};

export default HotelFullTemplateForm;
